/**
 * PlayerCardModal - Full player card display in a modal
 *
 * Displays the player card with navigation through four pages:
 * 1. Founder Snapshot - Photo, name, title, quick bio
 * 2. Vision & Purpose - Vision, what to change, purpose, unique approach
 * 3. Company Overview - Company details and offering
 * 4. Company Highlights - Metrics and funding snapshot
 *
 * Matches the design from Founder Player Card - Template.pdf
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShareIcon from '@mui/icons-material/Share';
import PersonIcon from '@mui/icons-material/Person';
import { ENTITY_TYPE_CONFIG } from './demoResources';
import { TabPanel, ENTITY_ICONS, BRAND_COLORS, getInitials } from './playerCardShared';

// eslint-disable-next-line max-lines-per-function
function PlayerCardModal({
  open,
  onClose,
  entity,
  playerCardData,
  onOpenFull,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [currentTab, setCurrentTab] = useState(0);

  if (!entity || !playerCardData) {
    return null;
  }

  const { snapshot, vision, company, highlights, entityType } = playerCardData;
  const EntityIcon = ENTITY_ICONS[entityType] || PersonIcon;
  const entityConfig = ENTITY_TYPE_CONFIG[entityType] || ENTITY_TYPE_CONFIG.person;

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/care/player-card/${entity.id}`;
    navigator.clipboard.writeText(url);
    // Could add a toast notification here
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
          minHeight: fullScreen ? '100vh' : '600px',
          maxHeight: fullScreen ? '100vh' : '90vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: BRAND_COLORS.primary,
          color: 'white',
          p: 2,
          position: 'relative',
        }}
      >
        {/* Close and action buttons */}
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
          <IconButton
            onClick={handleShare}
            size="small"
            sx={{ color: 'white' }}
            title="Copy share link"
          >
            <ShareIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={onOpenFull}
            size="small"
            sx={{ color: 'white' }}
            title="Open full page"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Entity type badge */}
        <Chip
          icon={<EntityIcon sx={{ color: 'white !important', fontSize: 14 }} />}
          label={entityConfig.label}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.15)',
            color: 'white',
            mb: 1,
            '& .MuiChip-label': { fontSize: '0.75rem' },
          }}
        />

        {/* Faana label */}
        <Typography
          variant="overline"
          sx={{
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: '0.12em',
            mb: 0.5,
          }}
        >
          faana
        </Typography>

        {/* Page type label */}
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
          {entityType === 'person' ? 'Founder' : entityConfig.label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND_COLORS.secondary }}>
          Snapshot
        </Typography>
      </Box>
      {/* Tab Navigation */}
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            minHeight: 48,
          },
          '& .Mui-selected': {
            color: BRAND_COLORS.secondary,
          },
          '& .MuiTabs-indicator': {
            bgcolor: BRAND_COLORS.secondary,
          },
        }}
      >
        <Tab label="Snapshot" />
        <Tab label="Vision" />
        <Tab label="Company" />
        <Tab label="Highlights" />
      </Tabs>
      {/* Content */}
      <DialogContent sx={{ p: 0, bgcolor: BRAND_COLORS.primary }}>
        {/* Page 1: Snapshot */}
        <TabPanel value={currentTab} index={0}>
          <Box sx={{ p: 3, color: 'white', minHeight: 400 }}>
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Avatar
                src={snapshot.photoUrl}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: BRAND_COLORS.secondary,
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  border: '4px solid',
                  borderColor: BRAND_COLORS.secondary,
                }}
              >
                {getInitials(snapshot.name)}
              </Avatar>
              <Box sx={{ pt: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'white' }}>
                  {snapshot.name}
                </Typography>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {snapshot.title}
                  {snapshot.organization && `, ${snapshot.organization}`}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: BRAND_COLORS.secondary,
                borderRadius: 4,
                px: 3,
                py: 1,
                display: 'inline-block',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                Quick Bio:
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: 'rgba(255,255,255,0.9)',
                fontSize: '1rem',
              }}
            >
              {snapshot.quickBio}
            </Typography>
          </Box>
        </TabPanel>

        {/* Page 2: Vision & Purpose */}
        <TabPanel value={currentTab} index={1}>
          <Box sx={{ p: 3, color: 'white', minHeight: 400 }}>
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 1 }}>
                    Vision of a Better Place
                  </Typography>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
                    <Typography variant="body2" sx={{ color: '#1a1a1a !important' }}>
                      {vision.betterPlace || 'Not yet defined'}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 1 }}>
                    What You Aim to Change
                  </Typography>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
                    <Typography variant="body2" sx={{ color: '#1a1a1a !important' }}>
                      {vision.aimToChange || 'Not yet defined'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 1 }}>
                    Purpose & Point
                  </Typography>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
                    <Typography variant="body2" sx={{ color: '#1a1a1a !important' }}>
                      {vision.purpose || 'Not yet defined'}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, fontWeight: 700, mb: 1 }}>
                    Unique Approach
                  </Typography>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
                    <Typography variant="body2" sx={{ color: '#1a1a1a !important' }}>
                      {vision.uniqueApproach || 'Not yet defined'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Page 3: Company Overview */}
        <TabPanel value={currentTab} index={2}>
          <Box sx={{ p: 3, color: 'white', minHeight: 400 }}>
            <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'white' }}>
                Company
              </Typography>
              <Box>
                <Grid container spacing={1} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  <Grid size={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Company Name:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                      {company.name || '-'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Industry:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                      {company.industry || '-'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Founded:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                      {company.founded || '-'}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>HQ:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                      {company.headquarters || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
              Overview
            </Typography>

            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
              Tagline:
            </Typography>
            <Typography variant="h6" sx={{ color: BRAND_COLORS.secondary, mb: 2 }}>
              {company.tagline || 'No tagline'}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                Core Offering / Product:
              </Typography>
              <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
                <Typography variant="body2" sx={{ color: '#1a1a1a !important' }}>
                  {company.coreOffering || 'Not yet defined'}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                Target Customer / Market:
              </Typography>
              <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
                <Typography variant="body2" sx={{ color: '#1a1a1a !important' }}>
                  {company.targetMarket || 'Not yet defined'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Page 4: Company Highlights */}
        <TabPanel value={currentTab} index={3}>
          <Box sx={{ p: 3, color: 'white', minHeight: 400 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center', color: 'white' }}>
              Company Highlights
            </Typography>

            {/* Top metrics row */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {[
                { label: 'Revenue', value: highlights.revenue },
                { label: 'Customers', value: highlights.customers },
                { label: 'Partnerships', value: highlights.partnerships },
                { label: 'Milestones', value: highlights.milestones },
                { label: 'Awards', value: highlights.awards },
              ].map((metric) => (
                <Grid
                  key={metric.label}
                  size={{
                    xs: 6,
                    sm: 2.4
                  }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                      {metric.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {metric.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Metrics cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {Object.entries(highlights.metrics).map(([key, value]) => (
                <Grid
                  key={key}
                  size={{
                    xs: 6,
                    sm: 4,
                    md: 2.4
                  }}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Funding Snapshot */}
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center', color: 'white' }}>
              Funding Snapshot
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(highlights.funding).map(([key, value]) => (
                <Grid
                  key={key}
                  size={{
                    xs: 12,
                    sm: 4
                  }}>
                  <Box
                    sx={{
                      bgcolor: BRAND_COLORS.secondary,
                      borderRadius: 3,
                      p: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, px: 1 }}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      p: 2,
                      minHeight: 60,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>
      </DialogContent>
      {/* Footer */}
      <Box
        sx={{
          bgcolor: BRAND_COLORS.secondary,
          py: 1,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            fontWeight: 500,
            letterSpacing: '0.05em',
          }}
        >
          faanaworks.com
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: currentTab === i ? 'white' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
              }}
              onClick={() => setCurrentTab(i)}
            />
          ))}
        </Box>
      </Box>
    </Dialog>
  );
}

export default PlayerCardModal;
